<script lang="ts">
    import { getContext, onDestroy, onMount, setContext } from "svelte";
    import { writable, type Writable } from "svelte/store";
    import { CellState, Routes } from "@lib/types";
    import type { Timer, Puzzle, Attempt } from "@lib/types";
    import { db } from "@lib/db";
    import { solve } from "@lib/api";
    import { logEvent } from "@lib/events";
    import { isSolved, difficultyGroupName, PuzzleDate } from "@lib/util";
    import Clock from "./Clock.svelte";
    import Grid from "./Grid.svelte";
    import HintButton from "./HintButton.svelte";
    import HintPanel from "./HintPanel.svelte";
    import ResetButton from "./ResetButton.svelte";
    import StepControl from "./StepControl.svelte";

    interface Props {
        puzzle: Puzzle;
        attempt: Attempt;
    }

    let { puzzle, attempt }: Props = $props();

    const router = getContext<Writable<Routes>>("router");
    const date = getContext<Writable<PuzzleDate>>("date");

    const timer = writable<Timer>(attempt.timer);
    const placements = writable<Array<CellState>>(attempt.placements);
    const snapshots = writable<Array<Array<CellState>>>(attempt.snapshots);
    const hintCells = writable<Array<number>>([]);
    const hintText = writable<string | null>(null);
    const hintCooldownUntil = writable<number>(attempt.hint_cooldown_until ?? 0);
    const puzzleError = writable<string | null>(null);
    const heatmap = writable<number[] | null>(null);
    const kingOrder = writable<number[] | null>(null);
    const markerRate = writable<number[] | null>(null);

    setContext("puzzle", puzzle);
    setContext("session", attempt.session);
    setContext("timer", timer);
    setContext("placements", placements);
    setContext("snapshots", snapshots);
    setContext("hintCells", hintCells);
    setContext("hintText", hintText);
    setContext("hintCooldownUntil", hintCooldownUntil);
    setContext("puzzleError", puzzleError);
    setContext("heatmap", heatmap);
    setContext("kingOrder", kingOrder);
    setContext("markerRate", markerRate);

    let failedToSolve = $state(false);

    const handleSolve = async () => {
        const solution = isSolved(puzzle, $placements);
        if (!solution) return;

        void logEvent({
            session: attempt.session,
            event_type: "solve_submitted",
            elapsed_ms: $timer.elapsed,
            data: { solution },
        });

        try {
            await solve(attempt.session, solution, $timer.elapsed);

            void logEvent({
                session: attempt.session,
                event_type: "solve_result",
                elapsed_ms: $timer.elapsed,
                data: { ok: true },
            });

            puzzleError.set(null);

            if (!attempt.solved) {
                await db.attempts.update(attempt.id, { solved: true });
                router.set(Routes.Menu);

                if (typeof window !== "undefined") {
                    const currentState = window.history.state || {};
                    window.history.replaceState(
                        {
                            ...currentState,
                            page: Routes.Menu,
                            date: $date,
                        },
                        "Menu",
                        "",
                    );
                }
            }
        } catch (e) {
            // Offer a retry button, pause timer etc.
            timer.update((t: Timer) => ({ ...t, paused: true }));
            const message =
                e instanceof Error && e.message
                    ? e.message
                    : "Failed to submit solution";

            void logEvent({
                session: attempt.session,
                event_type: "solve_result",
                elapsed_ms: $timer.elapsed,
                data: { ok: false, error: message },
            });

            puzzleError.set(message);
            failedToSolve = true;
        }
    };

    onDestroy(
        timer.subscribe(async (t) => {
            try {
                await db.attempts.update(attempt.id, {
                    timer: $state.snapshot(t),
                });
            } catch (e) {
                // noop
            }
        }),
    );

    onDestroy(
        hintCooldownUntil.subscribe(async (until) => {
            if (!attempt.id) return;

            try {
                await db.attempts.update(attempt.id, {
                    hint_cooldown_until: until,
                });
            } catch (e) {
                // noop
            }
        }),
    );

    onDestroy(
        placements.subscribe(async (p) => {
            try {
                await db.attempts.update(attempt.id, {
                    placements: $state.snapshot(p),
                });
            } catch (e) {
                // noop
            }

            // Only clear the current hint once all highlighted cells have been acted on.
            const currentHintCells = $hintCells;
            if (currentHintCells.length > 0) {
                const allSatisfied = currentHintCells.every((idx) => {
                    const cell = p[idx];
                    return cell !== CellState.Empty;
                });

                if (allSatisfied) {
                    hintCells.set([]);
                    hintText.set(null);
                }
            }

            handleSolve();
        }),
    );

    onDestroy(
        snapshots.subscribe(async (s) => {
            try {
                await db.attempts.update(attempt.id, {
                    snapshots: $state.snapshot(s),
                });
            } catch (e) {
                // noop
            }
        }),
    );

    const onVisibilityChange = () => {
        if (!attempt.solved && document.visibilityState === "hidden") {
            timer.update((t: Timer) => ({
                ...t,
                paused: true,
            }));
        }
    };

    onMount(() => {
        timer.update((t: Timer) => ({ ...t, paused: attempt.solved }));
    });

    onDestroy(() => {
        timer.update((t: Timer) => ({ ...t, paused: true }));
    });
</script>

<svelte:document onvisibilitychange={onVisibilityChange} />

{#if failedToSolve}
    <button class="link" type="button" onclick={handleSolve}>
        Unexpected error occured, click to retry
    </button>
{/if}

<section class="puzzle" class:has-hint={$hintCells.length > 0}>
    {#if $puzzleError}
        <div class="error-banner">{$puzzleError}</div>
    {/if}
    <div class="controls">
        <div class="left">
            {#if !attempt.solved}
                <Clock />
            {/if}
        </div>
        <div class="center">
            {difficultyGroupName(puzzle.difficulty)}
        </div>
        <div class="right">
            {#if !$timer.paused}
                <HintButton />
            {/if}
        </div>
    </div>

    <Grid
        size={puzzle.size}
        readonly={$timer.paused}
        cloak={$timer.paused && !attempt.solved}
    />

    <div class="controls">
        {#if !$timer.paused}
            <div class="left">
                <ResetButton />
            </div>
            <div class="right">
                <StepControl />
            </div>
        {/if}
    </div>

    <HintPanel text={$hintText} />

    {#if !attempt.solved}
        <div class="instructions">
            <p>
                <b>INSTRUCTIONS:</b> Press to place a marker. Press again to place
                a king. Press and drag to place multiple markers.
            </p>

            <p>
                <b>OBJECTIVE:</b> Place 2 kings in each row, column, and region.
                Kings cannot be placed in adjacent cells.
            </p>
        </div>
    {/if}
</section>
